import { Form } from 'antd';
import classNames from 'classnames';
import React, { FC, useCallback, useEffect } from 'react';
import {
  AnnotationStandardDetails,
  FindingTypeDetails,
  FindingTypeProperty,
  FindingTypeRuleDetails
} from '../../../AnnotationStandardForm/AnnotationStandardForm.slice';
import { AnnotationsForGrid, FindingTypeSuggestion, Localization } from '../../annotation.types';
import styles from '../AnnotationForm.module.scss';
import { CreateAnnotationProperty } from '../AnnotationForm.types';
import { getPropertyInitialValue, populateLocalizationProperties, preparePropertyGroupes, propertiesValueInitialization } from '../Helpers';
import FindingTypeSelect from './FindingTypeSelect';
import Properties from './Properties';
import { RootState } from '../../../../../rootReducer';
import { connect } from 'react-redux';

type Props = {
  annotationStandard: AnnotationStandardDetails | null;
  selectedFindingType: FindingTypeDetails | null;
  properties: CreateAnnotationProperty[][];
  selectedProperties: FindingTypeProperty[];
  selectedRules: FindingTypeRuleDetails[];
  showAssessmentBlock: boolean | null;
  distanceToRoot: string;
  annotationForEdit: AnnotationsForGrid | null;
  localization: Localization | null;
  localizationEditable: boolean;
  findingTypeChange: boolean;
  assessmentCompleted: boolean;
  setFindingTypeChange: (change: boolean) => void;
  setProperties: (properties: CreateAnnotationProperty[][]) => void;
  onChange: (id: string) => void;
  isWarrantyCaseClosed: boolean;
};

const AnnotationForm: FC<Props> = ({
  annotationStandard,
  selectedFindingType,
  properties,
  selectedProperties,
  selectedRules,
  showAssessmentBlock,
  distanceToRoot,
  annotationForEdit,
  localization,
  localizationEditable,
  findingTypeChange,
  assessmentCompleted,
  setFindingTypeChange,
  setProperties,
  onChange,
  isWarrantyCaseClosed
}) => {
  const getGroupes = useCallback(
    (newProperties: CreateAnnotationProperty[]) => {
      return preparePropertyGroupes(annotationStandard, newProperties, selectedFindingType);
    },
    [annotationStandard, selectedFindingType]
  );

  const populateLocalization = useCallback(
    (newProperties: CreateAnnotationProperty[][]) => {
      populateLocalizationProperties(newProperties, localization, selectedFindingType, localizationEditable && !assessmentCompleted);
      setProperties(newProperties);
    },
    [assessmentCompleted, localization, localizationEditable, selectedFindingType, setProperties]
  );

  useEffect(() => {
    if (selectedProperties && selectedProperties.length) {
      const propertiesForm: CreateAnnotationProperty[] = [];

      selectedProperties.forEach(property => {
        let isRequired = true;
        if (selectedFindingType) {
          const representations = selectedFindingType.representations;
          const representation = representations.find(x => x.findingTypePropertyId === property.id);
          if (representation) {
            isRequired = representation.isMandatory;
          }
        }

        let value = getPropertyInitialValue(property, distanceToRoot, annotationForEdit);
        let prop: CreateAnnotationProperty = {
          findingTypePropertyId: property.id,
          name: property.name,
          value: value,
          isReadonly: assessmentCompleted,
          label: property.fullName,
          required: isRequired
        };
        propertiesForm.push(prop);
      });

      const propertyGroupes = getGroupes(propertiesForm);

      if (!annotationForEdit) {
        populateLocalization(propertyGroupes!);
      } else {
        populateLocalizationProperties(propertyGroupes!, localization, selectedFindingType, localizationEditable && !assessmentCompleted);
      }

      propertiesValueInitialization(propertyGroupes, findingTypeChange);

      if (propertyGroupes) {
        setProperties(propertyGroupes);
      }
      setFindingTypeChange(false);
    }
  }, [
    annotationForEdit,
    annotationStandard,
    getGroupes,
    populateLocalization,
    selectedFindingType,
    selectedProperties,
    localization,
    localizationEditable,
    setFindingTypeChange,
    setProperties,
    distanceToRoot,
    assessmentCompleted
  ]);

  return (
    <>
      {annotationForEdit && annotationForEdit.findingTypeSuggestion && (
        <div className={styles.suggestedFindingType}>
          <label className={styles.label}>Suggested Finding type:</label>
          <span className={styles.value}>{FindingTypeSuggestion[annotationForEdit.findingTypeSuggestion]}</span>
        </div>
      )}
      {annotationForEdit && annotationForEdit.findingTypeMlSuggestion && (
        <div className={styles.suggestedFindingType}>
          <label className={styles.label}>ML Suggested Finding type:</label>
          <span className={styles.value}>{annotationForEdit.findingTypeMlSuggestion}</span>
        </div>
      )}
      <Form
        className={classNames(styles.createAnnotationFormContainer, { [styles.createAnnotationFormContainerWithAssessment]: showAssessmentBlock })}
        layout='vertical'
      >
        {annotationStandard && (
          <FindingTypeSelect
            disabled={assessmentCompleted || isWarrantyCaseClosed}
            selectedFindingType={selectedFindingType}
            findingTypes={annotationStandard.findingTypes}
            onChange={onChange}
          />
        )}
        <Properties
          {...{
            selectedFindingType,
            selectedProperties,
            selectedRules,
            properties,
            setProperties,
            annotationStandard,
            distanceToRoot,
            annotationForEdit,
            localization,
            localizationEditable,
            findingTypeChange,
            setFindingTypeChange,
            assessmentCompleted,
            isWarrantyCaseClosed
          }}
        />
      </Form>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isWarrantyCaseClosed: state.annotationForm.isWarrantyCaseClosed
});

export default connect(mapStateToProps)(AnnotationForm);
